/**
 * ReviewPage – post-session review.
 *
 * Calls POST /api/session/{id}/end if the session hasn't been reviewed yet,
 * otherwise fetches the stored review. Shows strengths, improvements,
 * phrase upgrades, vocabulary to save, and next recommendation.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CaretLeft,
  Confetti,
  ArrowUpRight,
  SparkleIcon,
  BookmarkSimple,
  ThumbsUp,
  Lightbulb,
  SpinnerGap,
} from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';

export default function ReviewPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingVocab, setSavingVocab] = useState(false);
  const [savedVocab, setSavedVocab] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Try fetching existing review; if missing, generate it.
        try {
          const { data } = await http.get(`/session/${sessionId}/review`);
          setReview(data);
        } catch {
          const { data } = await http.post(`/session/${sessionId}/end`);
          setReview(data);
        }
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  async function saveAllVocab() {
    if (!review?.vocabulary?.length) return;
    setSavingVocab(true);
    try {
      await http.post('/vocabulary/save', {
        session_id: sessionId,
        items: review.vocabulary,
      });
      setSavedVocab(true);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSavingVocab(false);
    }
  }

  if (loading) {
    return (
      <div className="lf-shell flex items-center justify-center h-screen">
        <div className="text-center text-[var(--lf-text-muted)]" data-testid="review-loading">
          <SpinnerGap size={32} className="mx-auto animate-spin text-[var(--lf-primary)]" />
          <div className="mt-3">Putting your review together…</div>
        </div>
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="lf-shell px-6 py-10">
        <div className="lf-card p-5" data-testid="review-error">
          <div className="text-[var(--lf-destructive)] font-semibold">{error || 'Review not available yet.'}</div>
          <Link to="/home" className="lf-btn lf-btn-primary mt-4 w-full">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="lf-shell px-6 py-8" data-testid="review-page">
      <div className="flex items-center gap-2">
        <Link to="/home" className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]" data-testid="review-back">
          <CaretLeft size={18} />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Session review</div>
          <h1 className="text-2xl font-bold">Nice work ✨</h1>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="lf-card mt-5 p-5"
        data-testid="review-overall"
      >
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 lf-chip lf-chip-primary" data-testid="review-cefr-chip">
            <Confetti size={12} weight="fill" /> CEFR {review.cefr_level || 'A1'}
          </div>
          <div className="inline-flex items-center gap-1 lf-chip">
            TOEFL {review.toefl_approx || '0-30'}
          </div>
        </div>
        <p className="mt-3 text-base leading-relaxed">{review.overall_feedback}</p>
        {review.cefr_reasoning ? (
          <p className="mt-2 text-xs italic text-[var(--lf-text-muted)]" data-testid="review-cefr-why">
            Why {review.cefr_level}: {review.cefr_reasoning}
          </p>
        ) : null}
      </motion.div>

      {/* Strengths */}
      {review.strengths?.length > 0 && (
        <Section icon={<ThumbsUp size={18} weight="duotone" />} title="What went well" testid="review-strengths">
          <ul className="space-y-2">
            {review.strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--lf-success)]" />
                {s}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Improvements */}
      {review.improvements?.length > 0 && (
        <Section icon={<Lightbulb size={18} weight="duotone" />} title="To work on next" testid="review-improvements">
          <ul className="space-y-2">
            {review.improvements.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--lf-accent)]" />
                {s}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Phrase upgrades */}
      {review.phrase_upgrades?.length > 0 && (
        <Section icon={<SparkleIcon size={18} weight="duotone" />} title="More natural ways to say it" testid="review-upgrades">
          <div className="space-y-3">
            {/* `phrase_upgrades` is a snapshot — never reordered after render —
                but using `better_version` as the key still avoids index-key
                lint warnings and is more robust to future edits. */}
            {review.phrase_upgrades.map((u) => (
              <div key={u.better_version} className="rounded-2xl bg-[var(--lf-surface-muted)] p-3">
                {u.your_version ? (
                  <div className="text-xs text-[var(--lf-text-muted)]">You said: <span className="text-[var(--lf-text)]">“{u.your_version}”</span></div>
                ) : null}
                <div className="mt-1 flex items-center gap-2 text-sm font-semibold">
                  <ArrowUpRight size={14} weight="bold" className="text-[var(--lf-primary)]" />
                  {u.better_version}
                </div>
                {u.why ? <div className="mt-1 text-xs text-[var(--lf-text-muted)]">{u.why}</div> : null}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Vocabulary */}
      {review.vocabulary?.length > 0 && (
        <Section icon={<BookmarkSimple size={18} weight="duotone" />} title="Vocabulary to save" testid="review-vocab">
          <div className="space-y-2">
            {/* `term` is the natural unique key here. */}
            {review.vocabulary.map((v) => (
              <div key={v.term} className="lf-card flex items-start gap-3 p-3">
                <div className="flex-1">
                  <div className="font-semibold">{v.term}</div>
                  <div className="text-xs text-[var(--lf-text-muted)]">{v.translation}</div>
                  {v.example ? <div className="mt-1 text-xs italic text-[var(--lf-text-muted)]">“{v.example}”</div> : null}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={saveAllVocab}
            disabled={savingVocab || savedVocab}
            className="lf-btn lf-btn-secondary mt-3 w-full"
            data-testid="review-save-vocab-btn"
          >
            {savingVocab ? <SpinnerGap size={16} className="animate-spin" /> : null}
            {savedVocab ? 'Saved!' : 'Save all to my vocabulary'}
          </button>
        </Section>
      )}

      {/* Next step */}
      {review.next_recommendation ? (
        <Section title="Next time, try…" testid="review-next">
          <p className="text-sm">{review.next_recommendation}</p>
        </Section>
      ) : null}

      <div className="mt-8 flex gap-3">
        <Link to="/home" className="lf-btn lf-btn-ghost flex-1" data-testid="review-home-btn">Home</Link>
        <Link to="/scenarios" className="lf-btn lf-btn-primary flex-[2]" data-testid="review-practice-btn">Practice again</Link>
      </div>
      <div className="h-10" />
    </div>
  );
}

function Section({ icon, title, children, testid }) {
  return (
    <div className="mt-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {icon ? <span className="text-[var(--lf-primary)]">{icon}</span> : null}
        <h3 className="text-sm font-bold uppercase tracking-widest text-[var(--lf-text-muted)]">{title}</h3>
      </div>
      <div className="lf-card p-4">
        {children}
      </div>
    </div>
  );
}
